import { useState } from "react";
import { MoreToggle } from "../../components/MoreToggle";
import type { Ownership as OwnershipRow } from "../../state/ownership";
import { asOfLabel, purchaseLabel, rightRows, splitLabel } from "./ownershipFormat";
import { A, Info, KV, SubHeader } from "./util";

export type OwnershipProps = {
  rows?: OwnershipRow[];
  // Perceelnaam boven elk blok: in een projectrapport of deellink staan
  // meerdere percelen onder elkaar.
  named?: boolean;
  // Rapport van een eigen projectperceel zonder levering: wijs de weg, maar
  // geen knop hier, want opvragen kost geld.
  hintWhenMissing?: boolean;
};

const Muted = ({ children }: { children: React.ReactNode }) => (
  <div className="p-2.5 text-gray-600">{children}</div>
);

const Annotations = ({ items }: { items: { aard: string; omschrijving: string }[] }) => {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <KV k={`Aantekeningen (${items.length})`}>
      <MoreToggle open={open} setOpen={setOpen} />
      {open && (
        <ul className="list-disc pl-4">
          {items.map((a, i) => (
            // Aantekeningen hebben geen id en kunnen gelijk luiden
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <li key={i}>{[a.aard, a.omschrijving].filter(Boolean).join(": ")}</li>
          ))}
        </ul>
      )}
    </KV>
  );
};

const Parcel = ({ row, named }: { row: OwnershipRow; named?: boolean }) => {
  const { summary } = row;
  const rights = rightRows(summary);
  const superseded = summary.overgegaan_in ?? [];
  const split = splitLabel(summary);
  const purchase = purchaseLabel(summary);
  const asOf = asOfLabel(row);
  return (
    <div className="py-1">
      <div className="px-2.5 py-1 text-gray-600">
        {named && <span className="font-semibold text-black">{row.parcelName} </span>}
        {asOf}
        {row.fileUrl && (
          <>
            {asOf && " · "}
            <A href={row.fileUrl}>PDF</A>
          </>
        )}
      </div>
      {superseded.length ? (
        <Muted>Vervallen, overgegaan in {superseded.join(", ")}.</Muted>
      ) : (
        rights.length > 0 && (
          // Vaste kolommen, net als de bekendmakingen: het rapport is smal.
          <table className="w-full table-fixed">
            <thead className="text-left text-gray-600">
              <tr>
                <th className="w-28 px-2.5 font-normal">Recht</th>
                <th className="w-16 px-1.5 font-normal">Aandeel</th>
                <th className="px-2.5 font-normal">Gerechtigde</th>
              </tr>
            </thead>
            <tbody>
              {rights.map((r, i) => (
                // Rechten hebben geen id; dezelfde gerechtigde kan twee rechten hebben
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <tr key={i} className="align-top">
                  <td className="px-2.5 truncate" title={r.kind}>
                    {r.kind}
                  </td>
                  <td className="px-1.5">{r.share}</td>
                  <td className="px-2.5 break-words">
                    {r.holder}
                    {r.detail && <div className="text-gray-600">{r.detail}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
      {/* Op de grond van een gesplitst perceel staan geen tenaamstellingen */}
      {!superseded.length && split && <Muted>{split}.</Muted>}
      <dl>
        <KV k="Koopsom" check={!!purchase}>
          {purchase}
        </KV>
        <KV k="Kadastrale grootte" check={summary.kadastrale_grootte_m2 != null}>
          {summary.kadastrale_grootte_m2?.toLocaleString("nl-NL")} m²
        </KV>
        <KV k="Gebruik" check={!!summary.omschrijving?.length}>
          {summary.omschrijving?.join(", ")}
        </KV>
        <Annotations items={summary.aantekeningen ?? []} />
      </dl>
    </div>
  );
};

/**
 * Eigendom volgens het Kadaster: uit een KIK-levering of een geüploade
 * Eigendomsinformatie-PDF. Alleen voor rechthebbenden en via de deellink; de
 * aanroeper haalt de regels op bij het endpoint dat daarbij hoort.
 */
export const Ownership = ({ rows, named, hintWhenMissing }: OwnershipProps) => {
  if (!rows) return null;
  if (!rows.length && !hintWhenMissing) return null;
  return (
    <>
      <SubHeader>Eigendom (Kadaster)</SubHeader>
      <Info>
        {rows.length ? (
          rows.map((row) => <Parcel key={row.parcelId} row={row} named={named} />)
        ) : (
          <Muted>Nog geen eigendomsinformatie; op te vragen onder Bestanden van het project.</Muted>
        )}
      </Info>
    </>
  );
};
